import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button, Space } from 'antd';
import { createStyles } from 'antd-style';
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { Markdown } from 'tiptap-markdown';

function getMarkdown(editor: Editor | null): string {
  if (!editor) return '';
  return (
    editor.storage as unknown as { markdown: { getMarkdown(): string } }
  ).markdown.getMarkdown();
}

const useStyles = createStyles(({ token, css }) => ({
  root: css`
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadius}px;
    background: ${token.colorBgContainer};
    overflow: hidden;
  `,
  toolbar: css`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    padding: 8px;
    border-bottom: 1px solid ${token.colorBorderSecondary};
    background: ${token.colorFillQuaternary};
  `,
  editor: css`
    max-height: var(--md-rich-max-height, 520px);
    overflow: auto;
    scrollbar-width: thin;

    .ProseMirror {
      min-height: var(--md-rich-min-height, 320px);
      outline: none;
      padding: 14px 16px;
      color: ${token.colorText};
      font-size: 14px;
      line-height: 1.72;
      word-break: break-word;
    }

    .ProseMirror > :first-child {
      margin-top: 0;
    }
    .ProseMirror > :last-child {
      margin-bottom: 0;
    }

    .ProseMirror h1,
    .ProseMirror h2,
    .ProseMirror h3,
    .ProseMirror h4 {
      color: ${token.colorTextHeading};
      font-weight: 650;
      line-height: 1.35;
      margin: 18px 0 10px;
    }
    .ProseMirror h1 {
      font-size: 22px;
    }
    .ProseMirror h2 {
      font-size: 18px;
      padding-bottom: 6px;
      border-bottom: 1px solid ${token.colorBorderSecondary};
    }
    .ProseMirror h3 {
      font-size: 16px;
    }

    .ProseMirror p,
    .ProseMirror ul,
    .ProseMirror ol,
    .ProseMirror blockquote,
    .ProseMirror pre {
      margin: 0 0 12px;
    }

    .ProseMirror ul,
    .ProseMirror ol {
      padding-left: 22px;
    }
    .ProseMirror li + li {
      margin-top: 4px;
    }
    .ProseMirror li > p {
      margin: 0;
    }

    .ProseMirror blockquote {
      border-left: 3px solid ${token.colorBorder};
      background: ${token.colorFillQuaternary};
      color: ${token.colorTextSecondary};
      padding: 8px 12px;
      border-radius: ${token.borderRadiusSM}px;
    }

    .ProseMirror code {
      background: ${token.colorFillSecondary};
      border: 1px solid ${token.colorBorderSecondary};
      border-radius: 4px;
      color: ${token.colorTextHeading};
      font-family: ${token.fontFamilyCode};
      font-size: 12px;
      padding: 1px 5px;
    }
    .ProseMirror pre {
      background: ${token.colorBgLayout};
      border: 1px solid ${token.colorBorderSecondary};
      border-radius: ${token.borderRadius}px;
      overflow: auto;
      padding: 12px;
    }
    .ProseMirror pre code {
      background: transparent;
      border: 0;
      padding: 0;
      white-space: pre;
    }

    .ProseMirror hr {
      border: 0;
      border-top: 1px solid ${token.colorBorderSecondary};
      margin: 16px 0;
    }
  `,
}));

export interface MarkdownRichEditorProps {
  value?: string;
  onChange?: (next: string) => void;
  className?: string;
  minHeight?: number;
  maxHeight?: number;
  showToolbar?: boolean;
}

export default function MarkdownRichEditor({
  value = '',
  onChange,
  className,
  minHeight = 320,
  maxHeight = 520,
  showToolbar = true,
}: MarkdownRichEditorProps) {
  const { styles, cx } = useStyles();
  const lastValueRef = useRef(value);

  const extensions = useMemo(
    () => [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Markdown.configure({
        html: false,
        breaks: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: value,
    editorProps: {
      attributes: {
        'data-placeholder': '输入内容…',
      },
    },
    onUpdate: ({ editor: current }) => {
      const next = getMarkdown(current);
      lastValueRef.current = next;
      onChange?.(next);
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (value === lastValueRef.current) return;
    const current = getMarkdown(editor);
    if (value !== current) {
      editor.commands.setContent(value, { emitUpdate: false });
      lastValueRef.current = value;
    }
  }, [editor, value]);

  const run = (fn: (editor: Editor) => void) => {
    if (!editor) return;
    fn(editor);
  };

  return (
    <div className={cx(styles.root, className)}>
      {showToolbar ? (
        <div className={styles.toolbar}>
          <Space size={4} wrap>
            <Button
              size="small"
              onClick={() =>
                run((e) => e.chain().focus().toggleHeading({ level: 1 }).run())
              }
            >
              H1
            </Button>
            <Button
              size="small"
              onClick={() =>
                run((e) => e.chain().focus().toggleHeading({ level: 2 }).run())
              }
            >
              H2
            </Button>
            <Button
              size="small"
              onClick={() =>
                run((e) => e.chain().focus().toggleHeading({ level: 3 }).run())
              }
            >
              H3
            </Button>
            <Button
              size="small"
              onClick={() => run((e) => e.chain().focus().setParagraph().run())}
            >
              正文
            </Button>
            <Button
              size="small"
              onClick={() => run((e) => e.chain().focus().toggleBold().run())}
            >
              加粗
            </Button>
            <Button
              size="small"
              onClick={() => run((e) => e.chain().focus().toggleItalic().run())}
            >
              斜体
            </Button>
            <Button
              size="small"
              onClick={() =>
                run((e) => e.chain().focus().toggleBulletList().run())
              }
            >
              列表
            </Button>
            <Button
              size="small"
              onClick={() =>
                run((e) => e.chain().focus().toggleOrderedList().run())
              }
            >
              编号
            </Button>
            <Button
              size="small"
              onClick={() =>
                run((e) => e.chain().focus().setHorizontalRule().run())
              }
            >
              分割线
            </Button>
          </Space>
        </div>
      ) : null}
      <EditorContent
        editor={editor}
        className={styles.editor}
        style={
          {
            '--md-rich-min-height': `${minHeight}px`,
            '--md-rich-max-height': `${maxHeight}px`,
          } as CSSProperties
        }
      />
    </div>
  );
}
